import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ApolloCache } from '@apollo/client';
import _get from 'lodash/get';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  AiCompanyIndexingCoverageChangedSubscription,
  AiCompanyIndexingCoverageDocument,
  AiCompanyIndexingCoverageQuery,
  useAiBudgetStatusQuery,
  useAiCompanyIndexingCoverageChangedSubscription,
  useAiCompanyIndexingCoverageQuery,
  useAiIndexingRetryParkedMutation,
} from 'graphql-common';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import useAiUsageByUserQueryHook from 'utils/fetch/hooks/useAiUsageByUserQueryHook';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import { getAllFeaturesUnlocked } from '@lib/components/UpgradePlanModal/utils/upgradePlanFeatureOptions';
import { PlanState } from '@lib/enums/plan';
import { OrderingParams, PaginationParams } from '@lib/enums/urls';
import { Modals } from '@lib/hooks/useModalManagement';
import useAiIndexingCoverageFrames from '@lib/hooks/useAiIndexingCoverageFrames';
import { useTabActivity } from '@lib/hooks/useTabActivity';
import { getErrorMessage } from '@lib/utils/graphQLErrors';
import toast from '@lib/components/Toast/Toast';
import {
  TOP_USERS_DEFAULT_ORDERING,
  TOP_USERS_PAGINATION,
} from './topUsersConstants';

export default function useActions(props: {
  closeModal: (name: string) => void;
}) {
  const { closeModal } = props;
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const {
    getUserAccessByCompany,
    handleOpenPlanModal,
    isFullAdmin,
    user,
  }: ContextProps = useOutletContext();

  const activeUsersCount = _get(user, 'company.activeUsersCount', 0) as number;
  const usersLimit = _get(user, 'company.plan.usersLimit') as number | null;
  const upgradeFeatures = _get(
    user,
    'company.plan.upgradeFeatures',
    [],
  ) as AccessComponentEnum[];

  const isAccessEnabled = (component: AccessComponentEnum) =>
    getUserAccessByCompany(component) === AccessLevelEnum.Write;
  // AI assistance reads the plan's granted feature list rather than an access
  // level — the signal the BE documents for this dashboard, and the one the
  // system-admin Accounts view already renders this badge from.
  const isAiAssistanceEnabled = upgradeFeatures.includes(
    AccessComponentEnum.AiAssistance,
  );

  const [topUsersOrderingParams, setTopUsersOrderingParams] =
    useState<OrderingParams>(TOP_USERS_DEFAULT_ORDERING);
  const [topUsersPaginationParams, setTopUsersPaginationParams] =
    useState<PaginationParams>(TOP_USERS_PAGINATION);
  // useState returns a fresh tuple on every render even when its value is
  // unchanged, which defeats AiUsageTopUsersTable's memo() — keep the tuple
  // identity stable so it only changes when the value it carries does.
  const topUsersOrderingState = useMemo<
    [OrderingParams, typeof setTopUsersOrderingParams]
  >(
    () => [topUsersOrderingParams, setTopUsersOrderingParams],
    [topUsersOrderingParams],
  );
  const topUsersPaginationState = useMemo<
    [PaginationParams, typeof setTopUsersPaginationParams]
  >(
    () => [topUsersPaginationParams, setTopUsersPaginationParams],
    [topUsersPaginationParams],
  );

  // Every AI query is plan-gated: without the feature they fail with
  // UPGRADE_PLAN_REQUIRED, so they must not run at all when it is off.
  const { data: budgetData } = useAiBudgetStatusQuery({
    fetchPolicy: 'cache-and-network',
    skip: !isAiAssistanceEnabled,
  });
  const {
    collection: topUsersCollection,
    firstLoading: topUsersFirstLoading,
    loading: topUsersLoading,
    totalCount: topUsersTotalCount,
  } = useAiUsageByUserQueryHook({
    orderingParams: topUsersOrderingState[0],
    paginationParams: topUsersPaginationState[0],
    skip: !isAiAssistanceEnabled,
  });
  // While the query is still resolving, assume there are users so the "View
  // all" action doesn't flash in once the count comes back.
  const hasTopUsers = topUsersFirstLoading || topUsersTotalCount > 0;
  const { data: indexingData, refetch: refetchIndexing } =
    useAiCompanyIndexingCoverageQuery({
      fetchPolicy: 'cache-and-network',
      skip: !isAiAssistanceEnabled,
    });

  // The card is otherwise fed only by the subscription below, so a cable drop
  // freezes it on whatever bucket counts arrived last — with no way for the
  // reader to tell a frozen card from an idle one. Reconcile on tab focus, the
  // same durable-counter catch-up JobTracker does.
  useTabActivity({
    onFocus: () => {
      if (isAiAssistanceEnabled) refetchIndexing();
    },
  });

  // Both the subscription frames and the retry-parked response are the whole
  // recomputed card, and both land in the same place: the query the card reads.
  const writeCoverage = useCallback(
    (cache: ApolloCache, coverage: AiCompanyIndexingCoverageQuery['data']) => {
      cache.writeQuery<AiCompanyIndexingCoverageQuery>({
        query: AiCompanyIndexingCoverageDocument,
        data: { data: coverage },
      });
    },
    [],
  );

  // The way out of `parked` without asking a developer to run a rake task:
  // every document automatic retries had given up on goes back to the frequent
  // pass, so the card comes back `parked: 0` / SCHEDULED. It does NOT repair the
  // cause — a file parked as unreadable fails again unless it is replaced —
  // which is why the reasons stay next to it.
  const [retryParked, { loading: retryParkedLoading }] =
    useAiIndexingRetryParkedMutation({
      // The mutation returns the recomputed card — write that, rather than
      // re-querying the aggregate the response already contains.
      update: (cache, { data }) => {
        const coverage = data?.data;
        if (coverage) writeCoverage(cache, coverage);
      },
      onCompleted: () => toast({ content: t('ai-retry-parked-success') }),
      onError: (mutationError) =>
        toast({ content: getErrorMessage(mutationError, t) }),
    });

  // A successful retry empties the parked set, so the list the modal is
  // showing stops existing — close it and let the recomputed card behind it
  // (parked: 0, SCHEDULED) be the answer.
  const onRetryParkedFromModal = useCallback(
    () =>
      retryParked().then((result) => {
        if (result.data) closeModal(Modals.CompanyPlanParkedModal);
        return result;
      }),
    [closeModal, retryParked],
  );

  // Indexing runs in the background — a system admin switching this company's AI
  // region re-indexes the whole corpus — so the card goes live rather than
  // showing a snapshot that silently ages: every bucket transition pushes a
  // frame, and the buckets drain visibly during the switch. Subscribing replays
  // the current snapshot, which is also what reconciles anything the query
  // missed, and frames land in the Apollo cache under the query, so the reads
  // below need no branch of their own.
  const coverageFrames = useAiIndexingCoverageFrames<
    AiCompanyIndexingCoverageChangedSubscription['data']
  >({
    label: 'CompanyPlan',
    onFrame: writeCoverage,
  });

  useAiCompanyIndexingCoverageChangedSubscription({
    skip: !isAiAssistanceEnabled,
    ...coverageFrames,
  });

  const breadCrumbsData = useMemo(
    () => getBreadCrumbsDataByKey({ pathname, t }),
    [pathname, t],
  );

  const onUpdatePlan = useCallback(
    () => handleOpenPlanModal(PlanState.Locked),
    [handleOpenPlanModal],
  );
  // Mirrors UpgradePlanModal's own "Contact our team" switch: once every
  // requestable feature is already unlocked there's nothing left to upgrade.
  const isAllFeaturesEnabled = getAllFeaturesUnlocked({
    getUserAccessByCompany,
  });

  return {
    activeUsersCount,
    breadCrumbsData,
    budgetData,
    hasTopUsers,
    indexingData,
    isAccessEnabled,
    isAiAssistanceEnabled,
    isAllFeaturesEnabled,
    isFullAdmin,
    onRetryParkedFromModal,
    onUpdatePlan,
    retryParked,
    retryParkedLoading,
    topUsersCollection,
    topUsersLoading,
    topUsersOrderingState,
    topUsersPaginationState,
    usersLimit,
  };
}
